// src/nexus/systemPulse.js
//
// Canonical System Pulse state — the SINGLE place that decides what "live",
// "partial", "Guardian mode", "N/M Services" etc. actually mean for the
// System Pulse cockpit and its diagnostic cards. No other component may
// invent its own status decision; every display reads fields off the object
// returned by `normalizeSystemPulseResponses()`.
//
// Root cause this closes (production report on https://www.dhsystemhqs.de/):
// the cockpit (`HqsCommandCenter`/`PulseView`) derived its numbers from
// `/api/hqs-live`, which (a) hardcodes `infrastructure.railway.
// successfulServices/totalServices` to 0 regardless of the real backend
// state, (b) never fetches operator-summary/world-state/operational-status/
// alerts at all, and (c) whose `sanitizeGuardian()` reads a guardian shape
// (`engineStatus.mode`, `stabilityScore`, `riskFlags`) that does not match
// the real `/api/admin/guardian-status-summary` response
// (`systemHealth`, `coverage.activeUniverseCount`, `worldStateSignals`,
// `pipeline`) — so Guardian always fell back to "UNKNOWN". Meanwhile the
// diagnostic cards (`LiveWorldPanel`/`useNexusWorldData`) independently
// fetch and classify the SAME seven endpoints correctly, producing "7/7
// live" — hence the contradiction.
//
// Verified against the REAL backend response shapes by reading the Railway
// route handlers directly (davidhuecker-bit/hqs-backend):
//   - GET /api/admin/frontend-critical-summary        -> services/frontendCriticalSummary.service.js#getRailwaySummary/toRailwayFormat
//       { success, healthy, data: { status, partial, stale, errors[], lastUpdated, metrics: { database, apis, services } } }
//       NOTE: `metrics.services` is a BOOLEAN, not a count. No service
//       counts, no universe size exist in this response at all.
//   - GET /api/admin/frontend-critical-summary/readiness -> same service#getRailwayReadiness
//       Same shape as above. NOTE: there is NO `ready` boolean anywhere in
//       this payload — the frontend's previous `readiness.data.ready` read
//       was always `undefined`.
//   - GET /api/admin/operator-summary                 -> services/operatorSummary.service.js#getOperatorSummary
//       { success, overallStatus, overallSeverity, attentionRequired, summary,
//         generatedAt, sections: { services: { count, summary: {
//           failedServicesCount, degradedServicesCount, waitingServicesCount,
//           neverRanServicesCount, ... } }, ... }, topIssues: [...] }
//       This is the ONLY validated source for real service counts.
//   - GET /api/admin/guardian-status-summary          -> services/guardianStatusSummary.builder.js#refreshGuardianStatusSummary
//       { success, systemHealth: "ok"|"degraded"|"critical",
//         worldStateFreshness, worldStateSignals: { earlyWarning, staleWarning, ... },
//         pipeline: { ok, stageSummary, oldestStageAgeMs },
//         coverage: { activeUniverseCount, universeTotalCount, ... },
//         dataQuality, proofStatus, agentConsensus, generatedAt }
//       This is the ONLY validated source for Universe size. There is no
//       `engineStatus`/`stabilityScore`/top-level `riskFlags`/`alerts` here.
//   - GET /api/admin/world-state                      -> { success, worldState: {...} }
//   - GET /api/admin/operational-status               -> { success, portfolioTwinState, sisReport,
//         operationalRelease, controlStatus, biggestBlockers: [...], nextStep, riskMode, generatedAt }
//   - GET /api/admin/alerts/summary                   -> services/monitoring/alertService.js#getAlertSummary
//       { success, bySeverity: { critical, warning, info }, byCategory: {...}, total }
//
// Every extractor below only trusts a field once (a) the owning endpoint's
// own per-response classification (`classifyEndpointResult` from
// `./nexusData.js`, itself `classifyPortfolioResponse` from
// `./portfolioData.js` — success/dataStatus/HTTP-status aware) says "live"
// or "partial", AND (b) the specific nested field has the expected type/
// shape. Anything else is left `null` and named in `missingFields` — never
// guessed, never defaulted to a fabricated 0.

/** @typedef {"live"|"partial"|"empty"|"error"|"unauthorized"|"timeout"|"unavailable"|"checking"} SystemPulseStatus */

export const SYSTEM_PULSE_STATUS = Object.freeze({
  LIVE: "live",
  PARTIAL: "partial",
  EMPTY: "empty",
  ERROR: "error",
  UNAUTHORIZED: "unauthorized",
  TIMEOUT: "timeout",
  UNAVAILABLE: "unavailable",
  CHECKING: "checking",
});

const ENDPOINT_ID = Object.freeze({
  CRITICAL_SUMMARY: "critical-summary",
  READINESS: "readiness",
  OPERATOR_SUMMARY: "operator-summary",
  GUARDIAN: "guardian",
  WORLD_STATE: "world-state",
  OPERATIONAL_STATUS: "operational-status",
  ALERTS_SUMMARY: "alerts-summary",
});

// A field may only be sourced from an endpoint result in one of these
// states — "checking"/"loading" means "no answer yet" (must not be treated
// as either live or failed), everything else in ENDPOINT_STATES (empty,
// unavailable, auth_*, degraded, timeout, error) is a genuine negative
// result and must never be mined for field content.
const USABLE_ENDPOINT_STATES = new Set(["live", "partial"]);

function findEndpoint(endpointResults, id) {
  if (!Array.isArray(endpointResults)) return null;
  return endpointResults.find((entry) => entry && entry.id === id) || null;
}

function isUsable(endpoint) {
  return !!endpoint && USABLE_ENDPOINT_STATES.has(endpoint.state);
}

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function isNonNegativeInteger(value) {
  return isFiniteNumber(value) && Number.isInteger(value) && value >= 0;
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

// Every one of the seven pulse endpoints replies with either a bare object
// or `{ data: <object> }` (unwrapBackendPayload's own convention — see
// `./nexusData.js`). Both shapes are accepted; nothing deeper is unwrapped
// here because each extractor below already names the exact nested path it
// expects on the real backend body.
function endpointBody(endpoint) {
  if (!endpoint) return null;
  const raw = endpoint.data;
  if (isPlainObject(raw)) return raw;
  return null;
}

function pushMissing(missingFields, field) {
  if (!missingFields.includes(field)) missingFields.push(field);
}

/* =========================================================
   PER-FIELD EXTRACTORS — each documents its own provenance
========================================================= */

function extractServices(endpointResults, missingFields, sources) {
  const endpoint = findEndpoint(endpointResults, ENDPOINT_ID.OPERATOR_SUMMARY);
  const provenance = { endpointId: ENDPOINT_ID.OPERATOR_SUMMARY, path: "sections.services" };
  if (!isUsable(endpoint)) {
    pushMissing(missingFields, "serviceLive");
    pushMissing(missingFields, "serviceTotal");
    sources.serviceLive = null;
    sources.serviceTotal = null;
    return { serviceLive: null, serviceTotal: null };
  }
  const body = endpointBody(endpoint);
  const services = body?.sections?.services;
  const total = services?.count;
  const summary = services?.summary;
  const failed = summary?.failedServicesCount;
  const degraded = summary?.degradedServicesCount;
  const waiting = summary?.waitingServicesCount;
  const neverRan = summary?.neverRanServicesCount;
  const allNumeric = [total, failed, degraded, waiting, neverRan].every(isFiniteNumber);
  if (!allNumeric) {
    pushMissing(missingFields, "serviceLive");
    pushMissing(missingFields, "serviceTotal");
    sources.serviceLive = null;
    sources.serviceTotal = null;
    return { serviceLive: null, serviceTotal: null };
  }
  // "Successful" = every tracked service NOT in one of the four reported
  // negative buckets (failed/degraded/waiting-for-data/never-run). These
  // four are mutually exclusive subsets of the same `count` total, so this
  // subtraction is exact given the fields the backend actually exposes —
  // never a guess, never re-derived from a different denominator.
  const live = Math.max(0, total - failed - degraded - waiting - neverRan);
  sources.serviceLive = provenance;
  sources.serviceTotal = provenance;
  return { serviceLive: live, serviceTotal: total };
}

function extractUniverse(endpointResults, missingFields, sources) {
  const endpoint = findEndpoint(endpointResults, ENDPOINT_ID.GUARDIAN);
  const provenance = { endpointId: ENDPOINT_ID.GUARDIAN, path: "coverage.activeUniverseCount/universeTotalCount" };
  if (!isUsable(endpoint)) {
    pushMissing(missingFields, "universe");
    sources.universe = null;
    return { active: null, total: null };
  }
  const body = endpointBody(endpoint);
  const coverage = body?.coverage;
  const active = coverage?.activeUniverseCount;
  const total = coverage?.universeTotalCount;
  if (!isFiniteNumber(active) || !isFiniteNumber(total)) {
    pushMissing(missingFields, "universe");
    sources.universe = null;
    return { active: null, total: null };
  }
  sources.universe = provenance;
  return { active, total };
}

const KNOWN_GUARDIAN_HEALTH = new Set(["ok", "degraded", "critical"]);

function extractGuardianState(endpointResults, missingFields, sources) {
  const endpoint = findEndpoint(endpointResults, ENDPOINT_ID.GUARDIAN);
  const provenance = { endpointId: ENDPOINT_ID.GUARDIAN, path: "systemHealth" };
  if (!endpoint || endpoint.state === "checking" || endpoint.state === "loading") {
    sources.guardianState = null;
    return "unknown";
  }
  if (!isUsable(endpoint)) {
    pushMissing(missingFields, "guardianState");
    sources.guardianState = null;
    return "unavailable";
  }
  const body = endpointBody(endpoint);
  const health = body?.systemHealth;
  if (typeof health !== "string" || !KNOWN_GUARDIAN_HEALTH.has(health)) {
    pushMissing(missingFields, "guardianState");
    sources.guardianState = null;
    return "unavailable";
  }
  sources.guardianState = provenance;
  return health;
}

// Real, validated risk signals only — never a heuristic guess. Bounded so a
// pathological backend list can't blow up the UI.
const MAX_RISK_FLAGS = 20;

function extractRiskFlags(endpointResults, missingFields, sources) {
  const flags = [];
  const usedSources = [];

  const guardianEndpoint = findEndpoint(endpointResults, ENDPOINT_ID.GUARDIAN);
  if (isUsable(guardianEndpoint)) {
    const body = endpointBody(guardianEndpoint);
    const signals = body?.worldStateSignals;
    if (isPlainObject(signals)) {
      if (signals.earlyWarning === true) flags.push("early_warning");
      if (signals.staleWarning === true) flags.push("world_state_stale");
      usedSources.push({ endpointId: ENDPOINT_ID.GUARDIAN, path: "worldStateSignals.earlyWarning/staleWarning" });
    }
    if (isPlainObject(body?.pipeline) && body.pipeline.ok === false) {
      flags.push("pipeline_unhealthy");
      usedSources.push({ endpointId: ENDPOINT_ID.GUARDIAN, path: "pipeline.ok" });
    }
  }

  const operatorEndpoint = findEndpoint(endpointResults, ENDPOINT_ID.OPERATOR_SUMMARY);
  if (isUsable(operatorEndpoint)) {
    const body = endpointBody(operatorEndpoint);
    if (Array.isArray(body?.topIssues)) {
      for (const issue of body.topIssues) {
        if (issue?.severity === "critical" && typeof issue?.item === "string") {
          flags.push(`operator:${issue.item}`);
        }
      }
      usedSources.push({ endpointId: ENDPOINT_ID.OPERATOR_SUMMARY, path: "topIssues[].severity=critical" });
    }
  }

  const operationalEndpoint = findEndpoint(endpointResults, ENDPOINT_ID.OPERATIONAL_STATUS);
  if (isUsable(operationalEndpoint)) {
    const body = endpointBody(operationalEndpoint);
    if (Array.isArray(body?.biggestBlockers)) {
      for (const blocker of body.biggestBlockers) {
        const label = typeof blocker === "string" ? blocker : blocker?.reason || blocker?.gate;
        if (typeof label === "string" && label) flags.push(`blocker:${label}`);
      }
      usedSources.push({ endpointId: ENDPOINT_ID.OPERATIONAL_STATUS, path: "biggestBlockers[]" });
    }
  }

  if (usedSources.length === 0) {
    pushMissing(missingFields, "riskFlags");
    sources.riskFlags = null;
    return [];
  }
  sources.riskFlags = usedSources;
  return flags.slice(0, MAX_RISK_FLAGS);
}

// Shared guard for both forecast-quality extractors below: only trusts
// `sections.forecastQuality.summary` when (a) operator-summary itself is
// live/partial, (b) the section is a real object, and (c) totalForecasts/
// verifiedForecasts are both finite, non-negative INTEGERS with
// verified <= total — a fractional, negative, string, NaN, or
// verified > total value is a contract violation, not "close enough", and
// must be treated exactly like a missing field (`null`, never clamped or
// coerced into looking plausible).
function readValidatedForecastQualitySummary(endpointResults) {
  const endpoint = findEndpoint(endpointResults, ENDPOINT_ID.OPERATOR_SUMMARY);
  if (!isUsable(endpoint)) return null;
  const body = endpointBody(endpoint);
  const summary = body?.sections?.forecastQuality?.summary;
  if (!isPlainObject(summary)) return null;
  const total = summary.totalForecasts;
  const verified = summary.verifiedForecasts;
  if (!isNonNegativeInteger(total) || !isNonNegativeInteger(verified) || verified > total) return null;
  return { summary, total, verified };
}

// "Open forecasts" = admitted forecasts not yet finally verified (total
// minus verified) — the real, current backlog size, sourced from the SAME
// operator-summary fetch System Pulse already performs for service counts
// (services/operatorSummary.service.js#buildForecastQualitySection). This
// replaces a static, historical placeholder number that used to be
// hardcoded in the UI; a missing/invalid field here must surface as `null`
// (rendered as "—"/"Aktueller Wert nicht geliefert"), never as the old
// static number or any other invented value.
function extractForecastQueue(endpointResults, missingFields, sources) {
  const provenance = { endpointId: ENDPOINT_ID.OPERATOR_SUMMARY, path: "sections.forecastQuality.summary.totalForecasts/verifiedForecasts" };
  const validated = readValidatedForecastQualitySummary(endpointResults);
  if (!validated) {
    pushMissing(missingFields, "forecastQueue");
    sources.forecastQueue = null;
    return { open: null };
  }
  sources.forecastQueue = provenance;
  return { open: validated.total - validated.verified };
}

// Real, connected forecast-quality figures — replaces the static
// verified-share and accuracy percentage literals that used to be
// hardcoded in IntelligenceView/LearningView/FORECAST_AUDIT. Both are
// sourced from the same already-fetched operator-summary section as
// extractForecastQueue(); either becomes `null` (never a fabricated
// percentage) when the section is missing/invalid or the specific field is
// wrong-typed.
function extractForecastQuality(endpointResults, missingFields, sources) {
  const provenance = { endpointId: ENDPOINT_ID.OPERATOR_SUMMARY, path: "sections.forecastQuality.summary.accuracy/verifiedForecasts/totalForecasts" };
  const validated = readValidatedForecastQualitySummary(endpointResults);
  if (!validated) {
    pushMissing(missingFields, "forecastQuality");
    sources.forecastQuality = null;
    return { accuracyPct: null, verifiedPct: null };
  }
  const rawAccuracy = validated.summary.accuracy;
  const accuracyPct = isFiniteNumber(rawAccuracy) && rawAccuracy >= 0 && rawAccuracy <= 1 ? rawAccuracy * 100 : null;
  const verifiedPct = validated.total > 0 ? (validated.verified / validated.total) * 100 : null;
  if (accuracyPct === null || verifiedPct === null) {
    pushMissing(missingFields, "forecastQuality");
    sources.forecastQuality = null;
    return { accuracyPct, verifiedPct };
  }
  sources.forecastQuality = provenance;
  return { accuracyPct, verifiedPct };
}

function extractAlerts(endpointResults, missingFields, sources) {
  const endpoint = findEndpoint(endpointResults, ENDPOINT_ID.ALERTS_SUMMARY);
  const provenance = { endpointId: ENDPOINT_ID.ALERTS_SUMMARY, path: "total/bySeverity" };
  if (!isUsable(endpoint)) {
    pushMissing(missingFields, "alerts");
    sources.alerts = null;
    return { total: null, bySeverity: null };
  }
  const body = endpointBody(endpoint);
  const total = body?.total;
  const bySeverity = body?.bySeverity;
  const severityValid = isPlainObject(bySeverity) &&
    ["critical", "warning", "info"].every((key) => isFiniteNumber(bySeverity[key]));
  if (!isFiniteNumber(total) || !severityValid) {
    pushMissing(missingFields, "alerts");
    sources.alerts = null;
    return { total: null, bySeverity: null };
  }
  sources.alerts = provenance;
  return { total, bySeverity: { critical: bySeverity.critical, warning: bySeverity.warning, info: bySeverity.info } };
}

// "ready" only when the readiness endpoint itself is live/partial AND its
// own real backend fields (`data.status === "operational"` and top-level
// `healthy === true`) both agree — never the non-existent `ready` boolean
// the frontend previously (and incorrectly) looked for.
function extractReadPathState(endpointResults, missingFields, sources) {
  const endpoint = findEndpoint(endpointResults, ENDPOINT_ID.READINESS);
  const provenance = { endpointId: ENDPOINT_ID.READINESS, path: "healthy/data.status" };
  if (!endpoint || endpoint.state === "checking" || endpoint.state === "loading") {
    sources.readPathState = null;
    return "checking";
  }
  if (!isUsable(endpoint)) {
    pushMissing(missingFields, "readPathState");
    sources.readPathState = null;
    return "blocked";
  }
  const body = endpointBody(endpoint);
  const healthy = body?.healthy === true;
  const status = body?.data?.status;
  if (typeof status !== "string") {
    pushMissing(missingFields, "readPathState");
    sources.readPathState = null;
    return "blocked";
  }
  sources.readPathState = provenance;
  return healthy && status === "operational" ? "ready" : "blocked";
}

// "ready" only when the critical-summary endpoint ITSELF reports
// healthy===true/data.status==="operational" — this is a SEPARATE real
// backend field set from the readiness endpoint's, even though both hit
// the same service and share an identical response shape. Before this
// extractor existed, critical-summary's payload was mined only for its
// transport-level state (used in computeOverallStatus's liveCount), never
// for its own healthy/status fields — so a live-at-the-transport-level but
// `healthy:false`/`status:"unknown"` critical-summary body could sit next
// to a genuinely "ready" readiness endpoint and the overall system would
// still report "live"/"connected"/"System kohärent", silently ignoring the
// one endpoint whose entire purpose is reporting the frontend's real
// operational status. See also extractReadPathState()'s identical logic.
function extractCriticalSummaryState(endpointResults, missingFields, sources) {
  const endpoint = findEndpoint(endpointResults, ENDPOINT_ID.CRITICAL_SUMMARY);
  const provenance = { endpointId: ENDPOINT_ID.CRITICAL_SUMMARY, path: "healthy/data.status" };
  if (!endpoint || endpoint.state === "checking" || endpoint.state === "loading") {
    sources.criticalSummaryState = null;
    return "checking";
  }
  if (!isUsable(endpoint)) {
    pushMissing(missingFields, "criticalSummaryState");
    sources.criticalSummaryState = null;
    return "blocked";
  }
  const body = endpointBody(endpoint);
  const healthy = body?.healthy === true;
  const status = body?.data?.status;
  if (typeof status !== "string") {
    pushMissing(missingFields, "criticalSummaryState");
    sources.criticalSummaryState = null;
    return "blocked";
  }
  sources.criticalSummaryState = provenance;
  return healthy && status === "operational" ? "ready" : "blocked";
}

// bridgeState ("connected"/"degraded"/"unknown") is the single Data Bridge
// signal every surface reads (topbar pill, "System kohärent" copy, buyer
// briefing). It requires BOTH the readiness probe AND the critical-summary
// endpoint itself to report ready — either one alone is not "the frontend
// read bridge is established".
function bridgeStateFromReadPath(readPathState, criticalSummaryState) {
  if (readPathState === "checking" || criticalSummaryState === "checking") return "unknown";
  if (readPathState === "ready" && criticalSummaryState === "ready") return "connected";
  return "degraded";
}

function mostRecentTimestamp(endpointResults) {
  let latest = null;
  for (const endpoint of endpointResults || []) {
    if (!isUsable(endpoint)) continue;
    const body = endpointBody(endpoint);
    const candidates = [body?.generatedAt, body?.data?.lastUpdated, body?.lastUpdated];
    for (const candidate of candidates) {
      if (typeof candidate !== "string") continue;
      const t = Date.parse(candidate);
      if (Number.isFinite(t) && (latest === null || t > Date.parse(latest))) latest = candidate;
    }
  }
  return latest;
}

const FRESHNESS_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

function computeFreshness(updatedAt, now) {
  if (!updatedAt) return "unknown";
  const t = Date.parse(updatedAt);
  if (!Number.isFinite(t)) return "unknown";
  return now - t <= FRESHNESS_MAX_AGE_MS ? "fresh" : "stale";
}

/* =========================================================
   OVERALL STATUS
========================================================= */

function computeOverallStatus({ endpointResults, readPathState, criticalSummaryState, guardianState, serviceLive, serviceTotal }) {
  const states = (endpointResults || []).map((entry) => entry?.state);
  if (states.length === 0) return SYSTEM_PULSE_STATUS.CHECKING;

  const allChecking = states.every((s) => s === "checking" || s === "loading");
  if (allChecking) return SYSTEM_PULSE_STATUS.CHECKING;

  const allOf = (...values) => states.every((s) => values.includes(s));
  if (allOf("auth_expired", "auth_forbidden")) return SYSTEM_PULSE_STATUS.UNAUTHORIZED;
  if (allOf("timeout")) return SYSTEM_PULSE_STATUS.TIMEOUT;
  if (allOf("unavailable")) return SYSTEM_PULSE_STATUS.UNAVAILABLE;
  if (allOf("error", "degraded")) return SYSTEM_PULSE_STATUS.ERROR;
  if (allOf("empty")) return SYSTEM_PULSE_STATUS.EMPTY;

  const liveCount = states.filter((s) => s === "live").length;
  // fix/pnl-dossier-mapping-and-live-truth: `criticalSummaryState` closes a
  // real contradiction — a genuinely unhealthy critical-summary body
  // (healthy:false/status:"unknown") at HTTP 200 used to be classified
  // "live" at the transport level and then never re-examined, so the
  // overall system could report LIVE/"connected"/"System kohärent" while
  // the frontend's own headline health endpoint said otherwise.
  const coreResolved =
    readPathState === "ready" &&
    criticalSummaryState === "ready" &&
    guardianState !== "unavailable" &&
    guardianState !== "unknown" &&
    serviceLive !== null &&
    serviceTotal !== null;

  if (liveCount === states.length && coreResolved) return SYSTEM_PULSE_STATUS.LIVE;
  return SYSTEM_PULSE_STATUS.PARTIAL;
}

function computeReason(status, { readPathState, criticalSummaryState, guardianState, missingFields }) {
  if (status === SYSTEM_PULSE_STATUS.LIVE) return null;
  if (status === SYSTEM_PULSE_STATUS.CHECKING) return "Erster Live-Abruf läuft noch.";
  if (readPathState === "blocked") return "Readiness-Endpoint meldet keinen betriebsbereiten Status (data.status/healthy).";
  if (criticalSummaryState === "blocked") return "Critical-Summary-Endpoint meldet healthy:false oder keinen betriebsbereiten Status.";
  if (guardianState === "unavailable") return "Guardian-Endpoint liefert kein bekanntes systemHealth-Feld.";
  if (missingFields.length > 0) return `Fehlende oder ungültige Felder: ${missingFields.join(", ")}.`;
  return "Mindestens ein Diagnose-Endpoint ist nicht live.";
}

/**
 * The single canonical System Pulse state. Every UI surface (cockpit,
 * diagnostic cards, topbar Data Bridge pill, contradiction banner) must
 * read from the object this returns — never compute `bridgeState`/
 * `guardianMode`/service counts independently.
 *
 * @param {object} params
 * @param {Array<{id:string,label:string,path:string,state:string,status:number,data:unknown,error:string|null,meta?:object}>} params.endpointResults
 *   The seven "pulse" world `EndpointResult`s from `useNexusWorldData("pulse")`
 *   (see `./LiveWorldPanel.jsx`), already classified via `classifyEndpointResult`.
 * @param {number} [params.now] Injectable clock for tests; defaults to Date.now().
 * @returns {object} canonical SystemPulseState
 */
export function normalizeSystemPulseResponses({ endpointResults = [], now = Date.now() } = {}) {
  const missingFields = [];
  const sources = {};

  const { serviceLive, serviceTotal } = extractServices(endpointResults, missingFields, sources);
  const universe = extractUniverse(endpointResults, missingFields, sources);
  const guardianState = extractGuardianState(endpointResults, missingFields, sources);
  const riskFlags = extractRiskFlags(endpointResults, missingFields, sources);
  const forecastQueue = extractForecastQueue(endpointResults, missingFields, sources);
  const forecastQuality = extractForecastQuality(endpointResults, missingFields, sources);
  const alerts = extractAlerts(endpointResults, missingFields, sources);
  const readPathState = extractReadPathState(endpointResults, missingFields, sources);
  const criticalSummaryState = extractCriticalSummaryState(endpointResults, missingFields, sources);
  const bridgeState = bridgeStateFromReadPath(readPathState, criticalSummaryState);

  const status = computeOverallStatus({ endpointResults, readPathState, criticalSummaryState, guardianState, serviceLive, serviceTotal });
  const updatedAt = mostRecentTimestamp(endpointResults);
  const freshness = computeFreshness(updatedAt, now);
  const reason = computeReason(status, { readPathState, criticalSummaryState, guardianState, missingFields });

  return {
    status,
    bridgeState,
    readPathState,
    criticalSummaryState,
    guardianState,
    serviceLive,
    serviceTotal,
    universe,
    riskFlags,
    forecastQueue,
    forecastQuality,
    alerts,
    updatedAt,
    freshness,
    reason,
    missingFields,
    sources,
  };
}

// Fields that are safe to carry forward from a previous good state when a
// newer fetch comes back partial/degraded for that SPECIFIC field — status/
// bridgeState/guardianState/readPathState always reflect the latest fetch
// truthfully and are never carried forward, since silently keeping an old
// "connected" while the current fetch says "blocked" would misrepresent the
// live read path.
const CARRYABLE_FIELDS = ["serviceLive", "serviceTotal", "universe", "riskFlags", "forecastQueue", "forecastQuality", "alerts"];

/**
 * Merges a freshly normalized state onto the previous one so that a
 * partial/degraded refresh can never blank out numbers a prior successful
 * fetch already established. Only the fields listed in `missingFields` for
 * the NEW state are candidates for carry-forward, and only when the
 * PREVIOUS state actually had a validated (non-missing) value for that
 * field — an old "unknown" can never be promoted into a fabricated value
 * either.
 *
 * @param {object|null} previous a prior `normalizeSystemPulseResponses()` result, or null on first load
 * @param {object} next the latest `normalizeSystemPulseResponses()` result
 */
export function mergeSystemPulseStates(previous, next) {
  if (!previous) return next;

  const merged = { ...next };
  const missingFields = [...next.missingFields];
  const sources = { ...next.sources };
  let carriedAnyStaleField = false;

  for (const field of CARRYABLE_FIELDS) {
    const stillMissing = next.missingFields.includes(field);
    const previouslyAvailable = !previous.missingFields.includes(field);
    if (!stillMissing || !previouslyAvailable) continue;
    merged[field] = previous[field];
    sources[field] = previous.sources[field];
    // No longer "missing" — it is carried, real, previously-validated data,
    // just not confirmed by the CURRENT fetch. Freshness communicates that.
    missingFields.splice(missingFields.indexOf(field), 1);
    carriedAnyStaleField = true;
  }

  merged.missingFields = missingFields;
  merged.sources = sources;
  if (carriedAnyStaleField && merged.freshness === "fresh") {
    merged.freshness = "stale";
  }
  return merged;
}

export const SYSTEM_PULSE_ENDPOINT_ID = ENDPOINT_ID;
